import { useEffect, useState, Fragment } from 'react';
import {
  Box,
  Container,
  Typography,
  Checkbox,
  List,
  ListItem,
  Divider,
  ListItemText,
  ListItemAvatar,
  Avatar,
  FormControlLabel,
  Modal,
  Button,
  useMediaQuery,
} from '@mui/material';
import Moment from 'react-moment';
import GradientCircularProgress from '../components/common/GradientCircularProgress.js';
import { fetchCognitoUserToken } from '../utils/user';
import { colors, fonts } from '../styles/fontStyles.js';
import { useThemeContext } from '../contexts/ThemeContext.js';
import { Link } from 'react-router-dom';

const backendURL = process.env.REACT_APP_BACKEND_URL;

export default function Messages() {
  const [messagesList, setMessagesList] = useState([]);
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [error, setError] = useState(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const { theme } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    maxHeight: '90vh',
    overflowY: 'auto',
    bgcolor: theme.custom.messages.modalStyle.backgroundColor,
    border: '0.5px solid #EEE',
    borderRadius: '20px',
    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
    p: 4,
    color: theme.custom.messages.modalStyle.color,
  };

  useEffect(() => {
    const fetchMessages = async () => {
      let access_token = await fetchCognitoUserToken();
      fetch(`${backendURL}/messages/`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json; charset=UTF-8', Authorization: 'Bearer ' + access_token },
      })
        .then((res) => {
          if (!res.ok) {
            setError(`Unable to load user messages: ${res.statusText}`);
            setErrorModalOpen(true);
          } else {
            res
              .json()
              .then((response) => {
                setMessagesList(response);
                setMessagesLoaded(true);
              })
              .catch((err) => {
                setError(`Error parsing response: ${err.message}`);
                setErrorModalOpen(true);
              });
          }
        })
        .catch((err) => {
          setError(`Error fetching messages: ${err.message}`);
          setErrorModalOpen(true);
        });
    };
    fetchMessages();
  }, []);

  const mlabelsAndIcons = {
    appointment: {
      label: 'Appointment',
      has_been_read_icon: {
        true: '/icons/inbox_calendar_read.svg',
        false: '/icons/inbox_calendar_unread.svg',
      },
    },
    pfas_test_order: {
      label: 'PFAS test order',
      has_been_read_icon: {
        true: '/icons/inbox_pfas_test_order_read.svg',
        false: '/icons/inbox_pfas_test_order_unread.svg',
      },
    },
    pfas_test_result: {
      label: 'PFAS test result',
      has_been_read_icon: {
        true: '/icons/inbox_pfas_test_result_read.svg',
        false: '/icons/inbox_pfas_test_result_unread.svg',
      },
    },
    order_dispatched: {
      label: 'PFAS test order dispatched',
      has_been_read_icon: {
        true: '/icons/inbox_order_dispatched_read.svg',
        false: '/icons/inbox_order_dispatched_unread.svg',
      },
    },
    order_at_delivery_company: {
      label: 'PFAS test order at delivery company',
      has_been_read_icon: {
        true: '/icons/inbox_order_at_delivery_company_read.svg',
        false: '/icons/inbox_order_at_delivery_company_unread.svg',
      },
    },
    order_transit_to_customer: {
      label: 'PFAS test order in transit',
      has_been_read_icon: {
        true: '/icons/inbox_order_transit_to_customer_read.svg',
        false: '/icons/inbox_order_transit_to_customer_unread.svg',
      },
    },
    order_delivered_to_customer: {
      label: 'PFAS test order delivered',
      has_been_read_icon: {
        true: '/icons/inbox_order_delivered_to_customer_read.svg',
        false: '/icons/inbox_order_delivered_to_customer_unread.svg',
      },
    },
    order_transit_to_lab: {
      label: 'PFAS test order in transit to LAB',
      has_been_read_icon: {
        true: '/icons/inbox_order_transit_to_lab_read.svg',
        false: '/icons/inbox_order_transit_to_lab_unread.svg',
      },
    },
    order_delivered_to_lab: {
      label: 'Order update',
      has_been_read_icon: {
        true: '/icons/inbox_order_delivered_to_lab_read.svg',
        false: '/icons/inbox_order_delivered_to_lab_unread.svg',
      },
    },
    update_app: {
      label: 'App Update Available',
      has_been_read_icon: {
        true: '/icons/inbox_update_app_read.svg',
        false: '/icons/inbox_update_app_unread.svg',
      },
    },
    upcoming_outage: {
      label: 'Planned Outage',
      has_been_read_icon: {
        true: '/icons/inbox_upcoming_outage_read.svg',
        false: '/icons/inbox_upcoming_outage_unread.svg',
      },
    },
  };

  const handleOpen = (message) => {
    setSelectedMessage(message);
    setOpen(true);
    if (!message['has_been_read']) {
      markMessageAsRead(message['mid']);
      message['has_been_read'] = true;
    }
  };

  const handleClose = () => setOpen(false);

  const markMessageAsRead = async (messageId) => {
    let access_token = await fetchCognitoUserToken();
    const endpoint = backendURL + '/messages/' + messageId + '/read';
    fetch(endpoint, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        Authorization: 'Bearer ' + access_token,
      },
    })
      .then((res) => {
        if (!res.ok) {
          setError(`Unable to mark message as read: ${res.statusText}`);
          setErrorModalOpen(true);
        }
      })
      .catch((err) => {
        setError(`Error marking message as read: ${err.message}`);
        setErrorModalOpen(true);
      });
  };

  const handleCheckboxChange = (event) => {
    setShowUnreadOnly(event.target.checked);
  };

  const filteredMessages = showUnreadOnly ? messagesList.filter((message) => !message.has_been_read) : messagesList;

  const showLoadingSpinner = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        margin: '20px 0',
      }}
    >
      <GradientCircularProgress />
    </Box>
  );

  return (
    <>
      <Container maxWidth="md"
        sx={{
          backgroundColor: theme.custom.messages.container.background,
        }}
      >
        {messagesLoaded && (
          <FormControlLabel
            control={
              <Checkbox
                checked={showUnreadOnly}
                onChange={handleCheckboxChange}
                sx={{
                  color: theme.custom.messages.showUnread.checkboxColor,
                  '& .MuiSvgIcon-root path': {
                    color: theme.custom.messages.showUnread.checkboxBorder,
                  },
                  '&.Mui-checked': {
                    color: theme.custom.messages.showUnread.checkboxCheckedColor,
                  },
                  '&.Mui-checked .MuiSvgIcon-root path': {
                    color: theme.custom.messages.showUnread.checkboxCheckedColor,
                  }
                }}
              />
            }
            label="Show unread"
            sx={{
              color: theme.custom.messages.showUnread.labelColor,
              marginLeft: 0,
              fontWeight: 600,
            }}
          />
        )}

        {!messagesLoaded ? (
          showLoadingSpinner()
        ) : (
          <List sx={{ width: '100%', bgcolor: 'transparent' }}>
            {filteredMessages.length > 0 ? (
              filteredMessages.map((message) => (
                <Fragment key={message['mid']}>
                  <ListItem
                    button
                    onClick={() => handleOpen(message)}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      padding: '16px',
                      borderRadius: '12px',
                      backgroundColor: theme.custom.messages.list.backgroundColor,
                      border: message['has_been_read'] ? 'transparent' : theme.custom.messages.list.borderUnread,
                      width: '100%',
                      flexWrap: 'nowrap',
                      marginBottom: '16px',
                    }}
                  >
                    <ListItemAvatar sx={{ alignSelf: 'flex-start', marginRight: '8px' }}>
                      <Avatar
                        src={mlabelsAndIcons[message['mtype']]['has_been_read_icon'][message['has_been_read']]}
                        sx={{ width: '24px', height: '24px' }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      sx={{
                        width: '100%',
                        flexGrow: 1,
                        marginTop: 0,
                      }}
                      primary={
                        <>
                          <Typography
                            component="div"
                            sx={{
                              color: theme.custom.messages.list.labelColor,
                              fontWeight: '500',
                              fontSize: '16px',
                            }}
                          >
                            {mlabelsAndIcons[message['mtype']]['label']}
                          </Typography>
                          <Typography
                            component="div"
                            sx={{
                              color: theme.custom.messages.list.subjectColor,
                              marginTop: '4px',
                              fontSize: '12px',
                              fontWeight: '400',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                            }}
                          >
                            {message['subject']}
                          </Typography>
                          <Typography
                            component="div"
                            sx={{
                              color: theme.custom.messages.list.createdAtColor,
                              fontSize: '12px',
                              marginTop: '4px',
                              fontWeight: '400',
                            }}
                          >
                            <Moment fromNow>{message['created_at']}</Moment>
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                </Fragment>
              ))
            ) : (
              <Typography sx={{ textAlign: 'center', color: theme.custom.messages.list.labelColor, fontSize: '14px', fontWeight: 400 }}>
                You don't have any messages yet.
              </Typography>
            )}
          </List>
        )}
      </Container>

      <Modal open={open} onClose={handleClose} aria-labelledby="modal-title" aria-describedby="modal-description">
        <Box sx={modalStyle}>
          {selectedMessage && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                  id="modal-title"
                  variant="h6"
                  component="h2"
                  sx={{
                    color: theme.custom.messages.modal.labelColor,
                    fontFamily: fonts.regular,
                    fontSize: '20px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '20px',
                    letterSpacing: '0.1px',
                  }}
                >
                  {mlabelsAndIcons[selectedMessage['mtype']]['label']}
                </Typography>
                <Typography
                  sx={{
                    color: theme.custom.messages.modal.labelColor,
                    fontFamily: fonts.regular,
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '20px',
                    letterSpacing: '0.25px',
                  }}
                >
                  {new Date(selectedMessage['created_at']).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}{' '}
                  {new Date(selectedMessage['created_at']).toLocaleDateString([], {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })}
                </Typography>
              </Box>
              <Divider sx={{ my: 2, bgcolor: theme.custom.messages.modal.dividerColor }} />
              <Typography
                id="modal-description"
                sx={{
                  textAlign: 'left',
                  mb: 2,
                  color: theme.custom.messages.modal.descriptionColor,
                  fontFamily: fonts.regular,
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '20px',
                  letterSpacing: '0.25px',
                  wordWrap: 'break-word',
                  '& a': {
                    color: theme.custom.messages.modal.linkColor,
                  },
                }}
                dangerouslySetInnerHTML={{ __html: selectedMessage['body'] }}
              />
              <Divider sx={{ my: 2, bgcolor: theme.custom.messages.modal.dividerColor }} />
              <Button
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  right: 16,
                  color: theme.custom.messages.modal.closeButtonColor,
                  fontFamily: fonts.titlesNavButtons,
                  fontSize: '20px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '20px',
                  letterSpacing: '0.1px',
                }}
              >
                Close
              </Button>
            </>
          )}
        </Box>
      </Modal>

      <Modal
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        aria-labelledby="error-modal-title"
        aria-describedby="error-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography id="error-modal-title" variant="h6" component="h2" sx={{ color: colors.gray100, mb: 2 }}>
            Error
          </Typography>
          <Typography id="error-modal-description" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button
            onClick={() => setErrorModalOpen(false)}
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 16,
              color: colors.gray100,
              fontFamily: fonts.titlesNavButtons,
              fontSize: '20px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '0.1px',
            }}
          >
            Close
          </Button>
        </Box>
      </Modal>
    </>
  );
}
